<template>
  <div
    class="relative min-h-screen w-screen overflow-hidden bg-white lg:grid lg:h-screen lg:grid-cols-[46%_54%]"
  >
    <main
      class="flex h-full w-full overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
      role="main"
    >
      <div class="mx-auto flex min-h-full w-full max-w-[620px] flex-col px-6 sm:px-10 lg:px-16">
        <div class="pt-8 sm:pt-10 lg:pt-12">
          <img :src="evidenceLogo" alt="Evidence" class="h-8 w-auto" />
        </div>

        <div class="flex flex-1 items-center py-10 sm:py-14 lg:py-10">
          <form class="w-full max-w-[420px] space-y-8" @submit.prevent="handleSubmit">
            <div class="space-y-3">
              <h1
                class="text-3xl font-semibold leading-tight text-gray-900 lg:text-[36px] lg:leading-10"
              >
                Forget Password?
              </h1>
              <p class="text-lg leading-7 text-gray-500 lg:text-xl">
                Enter your email address and we'll send you a link to reset your password.
              </p>
            </div>

            <div class="space-y-5">
              <div v-if="!isWaitingForCode" class="space-y-5">
                <TextInput
                  id="email"
                  ref="emailInput"
                  v-model="email"
                  label="Email"
                  type="email"
                  placeholder="Example@email.com"
                  :rules="[
                    (v) => !!v || 'Email is required',
                    (v) => /.+@.+\..+/.test(v) || 'Email must be valid',
                  ]"
                  @keyup.enter="requestRecoveryCode"
                />
              </div>
              <div v-else class="space-y-5">
                <TextInput
                  id="code"
                  ref="codeInput"
                  v-model="code"
                  label="Recovery Code"
                  type="text"
                  placeholder="Code"
                  :rules="[(v) => !!v || 'Code is required']"
                  @keyup.enter="loginWithRecoveryCode"
                />
              </div>
            </div>

            <button
              type="submit"
              class="w-full rounded-lg bg-[#2748B5] py-3 text-lg font-medium text-center text-white transition hover:bg-[#1c3aa9] disabled:opacity-50"
              :disabled="loading.isLoading.value"
              :aria-busy="loading.isLoading.value"
            >
              {{ !isWaitingForCode ? "Sign in" : "Verify Code" }}
            </button>

            <div class="flex items-center justify-center gap-2 border-t pt-8">
              <span class="text-xs tracking-widest text-[#9ca3af] uppercase">DEVELOPED BY</span>
              <img :src="betterDevicesLogo" class="h-6" alt="BetterDevices" />
            </div>
          </form>
        </div>

        <footer class="w-full max-w-[420px] pb-6 sm:pb-8">
          <div
            class="flex flex-col gap-2 text-xs text-[#6a7282] sm:flex-row sm:items-center sm:justify-between sm:text-sm"
          >
            <span>Copyright © {{ currentYear }} 4BetterDevices</span>
            <a class="cursor-pointer hover:underline">Privacy Policy</a>
          </div>
        </footer>
      </div>
    </main>

    <RightPanel />
  </div>
</template>

<script lang="ts" setup>
import { ref } from "vue";
import TextInput from "@app/components/Global/Inputs/TextInput.vue";
import RightPanel from "@app/components/Global/RightPanel.vue";
import { UsersService } from "@core/application/users.service";
import useSnackbar from "@app/composables/use-snackbar";
import { injectStrict } from "@app/utils/injectStrict";
import { UsersServiceKey } from "@app/injectionKeys";
import useLoading from "@app/composables/use-loading";
import { HttpException } from "@core/exceptions/http.exception";
import evidenceLogo from "@app/assets/images/evidence-logo.png";
import betterDevicesLogo from "@app/assets/images/4betterdevices-logo.png";

const usersService: UsersService = injectStrict(UsersServiceKey);
const email = ref("");
const code = ref("");
const snackbar = useSnackbar();
const loading = useLoading();
const isWaitingForCode = ref(false);
const emailInput = ref<InstanceType<typeof TextInput>>();
const codeInput = ref<InstanceType<typeof TextInput>>();
const flow = ref("");
const csrfToken = ref("");
const currentYear = new Date().getFullYear();

function handleSubmit() {
  if (isWaitingForCode.value) {
    void loginWithRecoveryCode();
    return;
  }

  void requestRecoveryCode();
}

async function requestRecoveryCode() {
  if (emailInput.value?.validate()) {
    loading.start();
    try {
      const [flowId, csrf] = await usersService.requestRecoveryCode(email.value.trim());
      flow.value = flowId;
      isWaitingForCode.value = true;
      csrfToken.value = csrf;
      snackbar.success("Password reset link sent to your email");
    } catch (e) {
      const error = e as HttpException;
      snackbar.error(error.message);
    } finally {
      loading.stop();
    }
  }
}

async function loginWithRecoveryCode() {
  const isCodeValid = codeInput.value?.validate();
  if (!isCodeValid) return;

  loading.start();
  try {
    await usersService.loginWithCode(code.value.trim(), flow.value, csrfToken.value);
    window.location.href = "/?reset-password=1";
  } catch (e) {
    const error = e as HttpException;
    snackbar.error(error.message);
  } finally {
    loading.stop();
  }
}
</script>
